import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import { useMembership } from '../../../hooks/MembershipProvider/MembershipProvider';
import styles from './Orders.module.css';

interface OrderInfo {
  id: string;
  customer_id: string;
  product_id: string;
  order_name?: string;
  payment_state: string;
  payment_key?: string;
  amount?: number;
  refund_amount?: number;
  is_gift?: boolean;
  phone_number?: string;
  customer_name?: string;
  created_at: string;
  updated_at?: string;
}

interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  start_index: number;
  end_index: number;
}

interface SearchFilters {
  search: string;
  payment_state: string;
  refund_amount: string;
  is_gift: string;
  min_amount: string;
  max_amount: string;
  created_after: string;
  created_before: string;
}

// 정렬 관련 인터페이스 추가
interface SortCondition {
  field: string;
  order: 'asc' | 'desc';
}

const Orders: React.FC = () => {
  const { getOrders, updateOrder, deleteOrder, cleanOrders } = useApi();
  const { user, hasAdminPermission, hasSystemPermission } = useMembership();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [orders, setOrders] = useState<OrderInfo[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    search: '',
    payment_state: '',
    refund_amount: '',
    is_gift: '',
    min_amount: '',
    max_amount: '',
    created_after: '',
    created_before: ''
  });
  
  // 정렬 상태 추가
  const [sortConditions, setSortConditions] = useState<SortCondition[]>([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deepCleanToggle, setDeepCleanToggle] = useState(false);

  // 주문 목록 조회
  const fetchOrders = useCallback(async (page: number = 1, filters: SearchFilters = searchFilters, size: number = pageSize, sorts: SortCondition[] = sortConditions) => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page,
        page_size: size,
        ...filters
      };
      
      // 정렬 파라미터 추가
      if (sorts.length > 0) {
        params.sort_by = sorts.map(sort => `${sort.field}:${sort.order}`);
      }
      
      // 빈 값 제거
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await getOrders(params);
      
      // 응답 데이터 검증 및 설정
      if (response && response.orders && response.pagination) {
        setOrders(response.orders);
        setPagination(response.pagination);
        setCurrentPage(page);
      } else {
        // 응답 형식이 예상과 다른 경우 빈 배열로 설정
        setOrders([]);
        setPagination(null);
        setCurrentPage(page);
      }
    } catch (err: any) {
      setError(err.message || '주문 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [getOrders, searchFilters, sortConditions]);

  // URL 파라미터에서 페이지 상태 복원
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    
    // 페이지 정보 복원
    const pageParam = searchParams.get('page');
    const pageSizeParam = searchParams.get('pageSize');
    
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
    
    if (pageSizeParam) {
      setPageSize(parseInt(pageSizeParam));
    }
    
    // 검색 필터 복원
    const newSearchFilters = { ...searchFilters };
    Object.keys(newSearchFilters).forEach(key => {
      const value = searchParams.get(key);
      if (value !== null) {
        (newSearchFilters as any)[key] = value;
      }
    });
    setSearchFilters(newSearchFilters);
    
    // 정렬 조건 복원
    const sortParam = searchParams.get('sort');
    if (sortParam) {
      const newSortConditions = sortParam.split(',').map(sortStr => {
        const [field, order] = sortStr.split(':');
        return { field, order: order as 'asc' | 'desc' };
      });
      setSortConditions(newSortConditions);
    }
  }, [location.search]);

  // 초기 로드 (URL 파라미터가 있으면 해당 페이지, 없으면 1페이지)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    const initialPage = pageParam ? parseInt(pageParam) : 1;
    
    fetchOrders(initialPage, searchFilters, pageSize, sortConditions);
  }, [fetchOrders, pageSize, sortConditions, location.search]);

  // 검색 필터 변경 핸들러
  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearOrders = async () => {
    console.log('deepCleanToggle', deepCleanToggle);
    await cleanOrders(deepCleanToggle);
    fetchOrders(1, searchFilters, pageSize, sortConditions);
  };

  // 검색 실행
  const handleSearch = () => {
    setCurrentPage(1);
    
    // URL 업데이트
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('pageSize', pageSize.toString());
    
    // 검색 필터 정보 추가
    Object.entries(searchFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.set(key, value.toString());
      }
    });
    
    // 정렬 조건 정보 추가
    if (sortConditions.length > 0) {
      const sortParams = sortConditions.map(sort => `${sort.field}:${sort.order}`);
      params.set('sort', sortParams.join(','));
    }
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    fetchOrders(1, searchFilters, pageSize, sortConditions);
  };

  // 검색 필터 초기화
  const handleResetFilters = () => {
    const resetFilters: SearchFilters = {
      search: '',
      payment_state: '',
      refund_amount: '',
      is_gift: '',
      min_amount: '',
      max_amount: '',
      created_after: '',
      created_before: ''
    };
    setSearchFilters(resetFilters);
    setCurrentPage(1);
    
    // URL 초기화
    navigate(location.pathname, { replace: true });
    
    fetchOrders(1, resetFilters, pageSize, sortConditions);
  };

  // 페이지 변경
  const handlePageChange = (page: number) => {
    // URL 업데이트
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    fetchOrders(page, searchFilters, pageSize, sortConditions);
  };

  // 페이지 크기 변경
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    
    // URL 업데이트
    const params = new URLSearchParams(location.search);
    params.set('pageSize', newPageSize.toString());
    params.set('page', '1');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    fetchOrders(1, searchFilters, newPageSize, sortConditions);
  };

  // 주문 상태 변경
  const handleUpdateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      await updateOrder(orderId, { payment_state: newStatus });
      // 상태 변경 후 현재 페이지 새로고침
      fetchOrders(currentPage, searchFilters, pageSize, sortConditions);
    } catch (err: any) {
      setError(err.message || '주문 상태 변경에 실패했습니다.');
    }
  };

  // 주문 상태 변경 (환불)
  const handleRefundOrder = async (orderId: string) => {
    try {
      await updateOrder(orderId, { payment_state: 'REFUNDED' });
      // 상태 변경 후 현재 페이지 새로고침
      fetchOrders(currentPage, searchFilters, pageSize, sortConditions);
    } catch (err: any) {
      setError(err.message || '주문 환불 처리에 실패했습니다.');
    }
  };

  // 주문 삭제
  const handleDeleteOrder = async (orderId: string) => {
    if (window.confirm('정말로 이 주문을 삭제하시겠습니까?')) {
      try {
        await deleteOrder(orderId);
        // 삭제 후 현재 페이지 새로고침
        fetchOrders(currentPage, searchFilters, pageSize, sortConditions);
      } catch (err: any) {
        setError(err.message || '주문 삭제에 실패했습니다.');
      }
    }
  };

  // 정렬 처리 함수
  const handleSort = (field: string) => {
    setSortConditions(prev => {
      const newSorts = [...prev];
      const existingIndex = newSorts.findIndex(sort => sort.field === field);
      
      if (existingIndex >= 0) {
        // 기존 정렬 조건이 있으면 순서 변경
        const currentOrder = newSorts[existingIndex].order;
        newSorts[existingIndex].order = currentOrder === 'asc' ? 'desc' : 'asc';
        
        // 해당 필드를 맨 앞으로 이동 (최고 우선순위)
        const [removed] = newSorts.splice(existingIndex, 1);
        newSorts.unshift(removed);
      } else {
        // 새로운 정렬 조건 추가 (맨 앞에 추가하여 최고 우선순위)
        newSorts.unshift({ field, order: 'asc' });
      }
      
      // 최대 3개까지만 유지
      if (newSorts.length > 3) {
        newSorts.splice(3);
      }
      
      // URL 업데이트
      const params = new URLSearchParams(location.search);
      if (newSorts.length > 0) {
        const sortParams = newSorts.map(sort => `${sort.field}:${sort.order}`);
        params.set('sort', sortParams.join(','));
      } else {
        params.delete('sort');
      }
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      
      return newSorts;
    });
  };

  // 정렬 순서 표시
  const getSortOrderDisplay = () => {
    if (sortConditions.length === 0) return null;
    
    return (
      <div className={styles.sortOrderDisplay}>
        <span>정렬 순서: </span>
        {sortConditions.map((sort, index) => (
          <span key={sort.field} className={styles.sortItem}>
            {index + 1}. {getFieldDisplayName(sort.field)} {sort.order === 'asc' ? '↑' : '↓'}
          </span>
        ))}
      </div>
    );
  };

  // 필드 표시명 변환
  const getFieldDisplayName = (field: string) => {
    const fieldNames: Record<string, string> = {
      'id': '주문 ID',
      'customer_id': '고객 ID',
      'product_id': '상품 ID',
      'order_name': '주문명',
      'payment_state': '결제 상태',
      'amount': '금액',
      'refund_amount': '환불 금액',
      'is_gift': '주문 유형',
      'phone_number': '전화번호',
      'created_at': '주문일',
      'updated_at': '수정일'
    };
    return fieldNames[field] || field;
  };

  // 정렬 헤더 컴포넌트
  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => {
    const currentSort = sortConditions.find(sort => sort.field === field);
    const isActive = currentSort !== undefined;
    const order = currentSort?.order;
    
    return (
      <th 
        className={`${styles.sortableHeader} ${isActive ? styles.active : ''}`}
        onClick={() => handleSort(field)}
      >
        <div className={styles.headerContent}>
          <span>{children}</span>
          <div className={styles.sortIndicator}>
            {isActive && (
              <span className={`${styles.sortArrow} ${order === 'asc' ? styles.active : ''}`}>
                {order === 'asc' ? '↑' : '↓'}
              </span>
            )}
            {!isActive && <span className={styles.sortPlaceholder}>↕</span>}
          </div>
        </div>
      </th>
    );
  };

  // 주문 상세보기
  const handleViewOrderDetail = (orderId: string) => {
    navigate(`/admin/orders/detail/${orderId}`, {
      state: { 
        from: '/admin/orders',
        fromName: '결제 관리',
        currentPage: currentPage,
        pageSize: pageSize,
        searchFilters: searchFilters,
        sortConditions: sortConditions
      }
    });
  };

  // 사용자 상세 페이지로 이동
  const handleViewUserDetail = (userId: string) => {
    navigate(`/admin/users/detail/${userId}`, {
      state: { 
        from: '/admin/orders',
        fromName: '결제 관리'
      }
    });
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // 금액 포맷팅
  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ko-KR').format(amount) + ' 원';
  };

  // 결제 상태 배지
  const getPaymentStateBadge = (state: string) => {
    const stateMap: { [key: string]: { text: string; className: string } } = {
      'ISSUED': { text: '대기', className: 'pending' },
      'PENDING': { text: '대기', className: 'pending' },
      'PAID': { text: '완료', className: 'paid' },
      'CANCELLED': { text: '취소', className: 'cancelled' },
      'REFUNDED': { text: '환불', className: 'refunded' },
      'FAILED': { text: '실패', className: 'failed' },
      'REJECTED': { text: '거부', className: 'failed' }
    };
    
    const stateInfo = stateMap[state] || { text: state, className: 'unknown' };
    
    return (
      <span className={`${styles.paymentStateBadge} ${styles[stateInfo.className]} w-full flex items-center justify-center`}>
        {stateInfo.text}
      </span>
    );
  };

  // 선물 여부 표시
  const getGiftBadge = (isGift?: boolean) => {
    if (isGift === undefined || isGift === null) return '-';
    
    return (
      <span className={`${styles.giftBadge} ${isGift ? styles.gift : styles.normal}`}>
        {isGift ? '선물' : '일반'}
      </span>
    );
  };

  return (
    <div className={styles.adminOrders}>
      <h1 className="admin-page-title">결제 관리</h1>
      
      {/* 검색 및 필터 섹션 */}
      <div className={styles.searchFiltersSection}>
        <div className={styles.filtersGrid}>
          {/* 검색어 */}
          <div className={styles.filterGroup}>
            <label htmlFor="search">검색어</label>
            <input
              type="text"
              id="search"
              placeholder="주문명, 고객ID, 상품ID, 전화번호"
              value={searchFilters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          {/* 결제 상태 */}
          <div className={styles.filterGroup}>
            <label htmlFor="payment_state">결제 상태</label>
            <select
              id="payment_state"
              value={searchFilters.payment_state}
              onChange={(e) => handleFilterChange('payment_state', e.target.value)}
            >
              <option value="">전체</option>
              <option value="PENDING">대기</option>
              <option value="PAID">완료</option>
              <option value="CANCELLED">취소</option>
              <option value="REFUNDED">환불</option>
              <option value="FAILED">실패</option>
            </select>
          </div>

          {/* 선물 여부 */}
          <div className={styles.filterGroup}>
            <label htmlFor="is_gift">주문 유형</label>
            <select
              id="is_gift"
              value={searchFilters.is_gift}
              onChange={(e) => handleFilterChange('is_gift', e.target.value)}
            >
              <option value="">전체</option>
              <option value="true">선물</option>
              <option value="false">일반</option>
            </select>
          </div>

          {/* 최소 금액 */}
          <div className={styles.filterGroup}>
            <label htmlFor="min_amount">최소 금액</label>
            <input
              type="number"
              id="min_amount"
              placeholder="0"
              value={searchFilters.min_amount}
              onChange={(e) => handleFilterChange('min_amount', e.target.value)}
            />
          </div>

          {/* 최대 금액 */}
          {/* <div className={styles.filterGroup}>
            <label htmlFor="max_amount">최대 금액</label>
            <input
              type="number"
              id="max_amount"
              placeholder="999999"
              value={searchFilters.max_amount}
              onChange={(e) => handleFilterChange('max_amount', e.target.value)}
            />
          </div> */}

          {/* 주문일 시작 */}
          {/* <div className={styles.filterGroup}>
            <label htmlFor="created_after">주문일 시작</label>
            <input
              type="date"
              id="created_after"
              value={searchFilters.created_after}
              onChange={(e) => handleFilterChange('created_after', e.target.value)}
            />
          </div> */}

          {/* 주문일 종료 */}
          <div className={styles.filterGroup}>
            <label htmlFor="created_before">주문일 종료</label>
            <input
              type="date"
              id="created_before"
              value={searchFilters.created_before}
              onChange={(e) => handleFilterChange('created_before', e.target.value)}
            />
          </div>
        </div>

        {/* 검색 버튼들 */}
        <div className={styles.filterActions}>
          <button className={styles.btnSearch} onClick={handleSearch} disabled={loading}>
            {loading ? '검색 중...' : '검색'}
          </button>
          <button className={styles.btnReset} onClick={handleResetFilters} disabled={loading}>
            필터 초기화
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* 페이지 크기 선택
      <div className={'w-full flex justify-between'}>
        <div className={styles.pageSizeSelector}>
          <label htmlFor="pageSize">페이지당 표시:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          >
            <option value={10}>10개</option>
            <option value={20}>20개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
          </select>
        </div>
      {getSortOrderDisplay()}
      </div> */}

      {/* 주문 테이블 */}
      <div className={styles.ordersTableContainer}>
        {loading ? (
          <div className={styles.loadingSpinner}>로딩 중...</div>
        ) : (
          <>
            <table className={styles.ordersTable}>
              <thead>
                <tr>
                  <th>번호</th>
                  <SortableHeader field="id">주문 ID</SortableHeader>
                  <SortableHeader field="customer_id">고객 ID</SortableHeader>
                  <SortableHeader field="product_id">상품 ID</SortableHeader>
                  <SortableHeader field="order_name">주문명</SortableHeader>
                  <SortableHeader field="payment_state">결제 상태</SortableHeader>
                  <SortableHeader field="amount">금액</SortableHeader>
                  <SortableHeader field="refund_amount">환불 금액</SortableHeader>
                  <SortableHeader field="is_gift">주문 유형</SortableHeader>
                  <SortableHeader field="phone_number">전화번호</SortableHeader>
                  <SortableHeader field="created_at">주문일</SortableHeader>
                  <th>액션</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={11} className={styles.noData}>
                      검색 결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* 실제 데이터 행들 */}
                    {orders.map((order, index) => {
                      // 현재 페이지를 고려한 실제 행 번호 계산
                      const rowNumber = pagination ? (pagination.current_page - 1) * pagination.page_size + index + 1 : index + 1;
                      
                      return (
                        <tr key={order.id}>
                          <td>{rowNumber}</td>
                          <td>{order.id}</td>
                          <td>
                            <button
                              onClick={() => handleViewUserDetail(order.customer_id)}
                              className={styles.userLinkBtn}
                              title="사용자 상세정보 보기"
                            >
                              { (order.customer_name !== null && order.customer_name !== '') ? order.customer_name : '회원'}
                            </button>
                          </td>
                          <td>{order.product_id}</td>
                          <td>{order.order_name || '-'}</td>
                          <td>{getPaymentStateBadge(order.payment_state)}</td>
                          <td>{formatAmount(order.amount)}</td>
                          <td>{formatAmount(order.refund_amount || 0)}</td>
                          <td>{getGiftBadge(order.is_gift)}</td>
                          <td>{order.phone_number || '-'}</td>
                          <td>{formatDate(order.created_at)}</td>
                          <td>
                            <button
                              className={styles.btnViewDetail}
                              onClick={() => handleViewOrderDetail(order.id)}
                            >
                              상세보기
                            </button>

                            {
                              ( order.payment_state === 'ISSUED' 
                              || order.payment_state === 'PENDING' 
                              || order.payment_state === 'FAILED' 
                              || order.payment_state === 'REJECTED' 
                              || order.payment_state === 'CANCELLED' ) && (
                                <button
                                  className={styles.btnFailed}
                                  onClick={() => handleDeleteOrder(order.id)}
                                >
                                  삭제
                                </button>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* 빈 행들 추가 (페이지 크기만큼 채우기) */}
                    {pagination && orders.length < pagination.page_size && Array.from({ length: pagination.page_size - orders.length }, (_, index) => {
                      const emptyRowNumber = pagination ? (pagination.current_page - 1) * pagination.page_size + orders.length + index + 1 : orders.length + index + 1;
                      
                      return (
                        <tr key={`empty-${index}`} className={styles.emptyRow}>
                          <td>{emptyRowNumber}</td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>

            {/* 페이지네이션 */}
            {pagination && pagination.total_pages > 0 && (
              <div className={"w-full flex justify-between items-center"}>
              <div className='flex tems-center px-4'>
                {/* 페이지 크기 선택 */}
                <div className={styles.pageSizeSelector}>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  >
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                    <option value={100}>100개</option>
                  </select>
                  <label htmlFor="pageSize">표시</label>
                </div>
              </div>

              <div className={styles.pagination}>
                <button
                  className={styles.btnPage}
                  disabled={!pagination.has_previous}
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                >
                  이전
                </button>
                
                {/* 페이지 번호들 */}
                {(() => {
                  const pages = [];
                  const totalPages = pagination.total_pages;
                  const currentPage = pagination.current_page;
                  
                  // 페이지가 1개인 경우
                  if (totalPages === 1) {
                    pages.push(1);
                  } else {
                    // 항상 첫 페이지 표시
                    pages.push(1);
                    
                    // 현재 페이지 주변 5개씩 표시
                    const range = 5;
                    const start = Math.max(2, currentPage - range);
                    const end = Math.min(totalPages - 1, currentPage + range);
                    
                    // 첫 페이지와 현재 페이지 범위 사이에 간격이 있는 경우 줄임표 추가
                    if (start > 2) {
                      pages.push('...');
                    }
                    
                    // 현재 페이지 주변 페이지들 표시
                    for (let i = start; i <= end; i++) {
                      if (i > 1 && i < totalPages) {
                        pages.push(i);
                      }
                    }
                    
                    // 현재 페이지 범위와 마지막 페이지 사이에 간격이 있는 경우 줄임표 추가
                    if (end < totalPages - 1) {
                      pages.push('...');
                    }
                    
                    // 마지막 페이지가 1이 아닌 경우에만 표시
                    if (totalPages > 1) {
                      pages.push(totalPages);
                    }
                  }
                  
                  return pages.map((page, index) => (
                    <React.Fragment key={index}>
                      {page === '...' ? (
                        <span className={styles.paginationEllipsis}>...</span>
                      ) : (
                        <button
                          className={`${styles.btnPage} ${page === currentPage ? styles.active : ''}`}
                          onClick={() => handlePageChange(page as number)}
                        >
                          {page}
                        </button>
                      )}
                    </React.Fragment>
                  ));
                })()}
                
                <button
                  className={styles.btnPage}
                  disabled={!pagination.has_next}
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                >
                  다음
                </button>
              </div>

              <div></div>
              </div>

            )}

            {/* 페이지 정보 */}
            {pagination && pagination.total_records > 0 && (
              <div className={styles.paginationInfo}>
                총 {pagination.total_records}개 중 {pagination.start_index}-{pagination.end_index}개 표시
                (페이지 {pagination.current_page}/{pagination.total_pages})
              </div>
            )}
          </>
        )}
      </div>

      {/* 관리자 및 시스템 권한을 모두 가진 사용자에게만 표시 */}
      {hasAdminPermission() && hasSystemPermission() && (
        <div className={styles.clearOrders}>
          <div className={styles.clearOrdersContent}>
            <div className={styles.clearOrdersInfo}>
              <div className={styles.clearOrdersTitle}>
                <div className={styles.clearOrdersIcon}>
                  🗑️
                </div>
                결제 목록 정리
              </div>
              <div className={styles.clearOrdersDescription}>
                완료된 주문 데이터를 정리하여 시스템 성능을 최적화합니다. 
                이 작업은 되돌릴 수 없으니 신중하게 진행해주세요.
              </div>
            </div>

            <div className={styles.deepCleanToggle}>
              <label className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={deepCleanToggle}
                  onChange={(e) => setDeepCleanToggle(e.target.checked)}
                  className={styles.toggleInput}
                />
                <span className={styles.toggleSlider}></span>
                <span className={styles.toggleText}>
                  심층 정리 (Deep Clean) - 사용자 데이터까지 정리
                </span>
              </label>
              <div className={styles.toggleDescription}>
                {deepCleanToggle ? 
                  "사용자 계정의 주문, 구독, 쿠폰 데이터까지 모두 정리합니다." :
                  "주문 테이블의 데이터만 정리합니다."
                }
              </div>
            </div>

            <button 
              className={styles.clearOrdersButton}
              onClick={handleClearOrders}
              disabled={loading}
            >
              {loading ? '정리 중...' : '정리하기'}
            </button>

          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
